import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../common/Toast';
import { createAssignment, type AssignmentCreationParams } from '../../services/assignmentsService';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { listTeacherClasses, listClassStudentIds, type TeacherClass } from '../../services/classesService';

interface Student {
  id: string;
  displayName: string;
  email: string;
}

// TeacherClass is imported from classesService — studentIds loaded lazily on select

interface AssignmentDialogProps {
  isOpen: boolean;
  onClose: () => void;
  quiz: {
    id: string;
    title: string;
    questions: any[];
  } | null;
  onAssignmentCreated?: (assignmentId: string) => void;
}

export default function AssignmentDialog({ 
  isOpen, 
  onClose, 
  quiz,
  onAssignmentCreated 
}: AssignmentDialogProps) {
  const { user, userDoc } = useAuth();
  const toast = useToast();

  const [students, setStudents] = useState<Student[]>([]);
  const [classes, setClasses] = useState<TeacherClass[]>([]);
  const [selectedStudents, setSelectedStudents] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [classesLoading, setClassesLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [assignmentMode, setAssignmentMode] = useState<'individual' | 'class'>('individual');

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    startAt: '',
    endAt: ''
  });

  // Load students and classes when dialog opens
  useEffect(() => {
    if (isOpen && user) {
      loadStudents();
      loadClasses();
      // Set default title if quiz is provided
      if (quiz) {
        setFormData(prev => ({
          ...prev,
          title: `${quiz.title} Assignment`
        }));
      }
    }
  }, [isOpen, user, quiz]);

  // Set default dates
  useEffect(() => {
    if (isOpen) {
      const now = new Date();
      const tomorrow = new Date(now);
      tomorrow.setDate(now.getDate() + 1);
      const nextWeek = new Date(now);
      nextWeek.setDate(now.getDate() + 7);

      setFormData(prev => ({
        ...prev,
        startAt: tomorrow.toISOString().slice(0, 16), // YYYY-MM-DDTHH:mm
        endAt: nextWeek.toISOString().slice(0, 16)
      }));
    }
  }, [isOpen]);

  const loadStudents = async () => {
    setLoading(true);
    try {
      // Query students assigned to this teacher (based on security rules structure)
      const studentsQuery = query(
        collection(db, 'users'),
        where('role', '==', 'student'),
        where('teacherId', '==', user!.uid)
      );

      const querySnapshot = await getDocs(studentsQuery);
      const studentsList: Student[] = querySnapshot.docs.map(doc => ({
        id: doc.id,
        displayName: doc.data().displayName || doc.data().email,
        email: doc.data().email
      }));

      console.log('Students loaded for teacher:', user!.uid, studentsList.length);
      setStudents(studentsList);
    } catch (error) {
      console.error('Error loading students:', error);
      toast.error('Failed to load students');
    } finally {
      setLoading(false);
    }
  };

  const loadClasses = async () => {
    setClassesLoading(true);
    try {
      const classesList = await listTeacherClasses(user!.uid);
      setClasses(classesList);
    } catch (error) {
      console.error('[AssignmentDialog] Error loading classes:', error);
      toast.error('Failed to load classes');
    } finally {
      setClassesLoading(false);
    }
  };

  const handleClassSelect = async (classId: string) => {
    const selectedClass = classes.find(c => c.id === classId);
    if (!selectedClass) return;

    // Lazily fetch student IDs if not yet loaded
    let studentIds = selectedClass.studentIds;
    if (!studentIds || studentIds.length === 0) {
      try {
        studentIds = await listClassStudentIds(user!.uid, classId, selectedClass.studentIds);
        // Cache in state so subsequent clicks don't re-fetch
        setClasses(prev => prev.map(c =>
          c.id === classId ? { ...c, studentIds } : c
        ));
      } catch (err) {
        console.error('[AssignmentDialog] Failed to load class members:', err);
        toast.error('Failed to load class members. Please try again.');
        return;
      }
    }

    const newSelectedStudents = new Set(selectedStudents);
    const allSelected = studentIds.every(id => selectedStudents.has(id));

    if (allSelected) {
      studentIds.forEach(id => newSelectedStudents.delete(id));
      toast.success(`Removed ${studentIds.length} students from ${selectedClass.name}`);
    } else {
      studentIds.forEach(id => newSelectedStudents.add(id));
      toast.success(`Added ${studentIds.length} students from ${selectedClass.name}`);
    }

    setSelectedStudents(newSelectedStudents);
  };

  const handleClearSelection = () => {
    setSelectedStudents(new Set());
    toast.success('Selection cleared');
  };

  const handleStudentToggle = (studentId: string) => {
    setSelectedStudents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(studentId)) {
        newSet.delete(studentId);
      } else {
        newSet.add(studentId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedStudents.size === students.length) {
      setSelectedStudents(new Set());
    } else {
      setSelectedStudents(new Set(students.map(s => s.id)));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!quiz || !user) return;

    if (selectedStudents.size === 0) {
      toast.error('Please select at least one student');
      return;
    }

    if (!formData.title.trim()) {
      toast.error('Please enter an assignment title');
      return;
    }

    const startDate = new Date(formData.startAt);
    const endDate = new Date(formData.endAt);

    if (endDate <= startDate) {
      toast.error('End date must be after start date');
      return;
    }

    setSubmitting(true);
    try {
      const params: AssignmentCreationParams = {
        ...(quiz.id && { quizId: quiz.id }), // Keep reference if available
        questions: quiz.questions, // ALWAYS store questions
        quizTitle: quiz.title, // Store original quiz title for reference
        teacherId: user.uid,
        schoolId: userDoc?.schoolId || 'default',
        title: formData.title.trim(),
        ...(formData.description.trim() && { description: formData.description.trim() }),
        studentIds: Array.from(selectedStudents),
        startAt: startDate,
        endAt: endDate
      };

      const assignmentId = await createAssignment(params);
      
      toast.success(`Assignment created for ${selectedStudents.size} students`);
      onAssignmentCreated?.(assignmentId);
      onClose();
      
      // Reset form
      setSelectedStudents(new Set());
      setFormData({
        title: '',
        description: '',
        startAt: '',
        endAt: ''
      });

    } catch (error) {
      console.error('Assignment creation failed:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to create assignment');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-900">Create Assignment</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl"
              disabled={submitting}
            >
              ×
            </button>
          </div>
          {quiz && (
            <p className="text-gray-600 mt-1">
              Quiz: <span className="font-medium">{quiz.title}</span> ({quiz.questions.length} questions)
            </p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {/* Assignment Details */}
          <div className="space-y-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Assignment Title *
              </label>
              <input
                type="text"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Chapter 5 Quiz"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description (optional)
              </label>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={2}
                placeholder="Assignment instructions or notes..."
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date & Time *
                </label>
                <input
                  type="datetime-local"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={formData.startAt}
                  onChange={(e) => setFormData(prev => ({ ...prev, startAt: e.target.value }))}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Date & Time *
                </label>
                <input
                  type="datetime-local"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={formData.endAt}
                  onChange={(e) => setFormData(prev => ({ ...prev, endAt: e.target.value }))}
                />
              </div>
            </div>
          </div>

          {/* Assignment Mode Selection */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-gray-700">
                Assignment Method ({selectedStudents.size} students selected)
              </label>
              {selectedStudents.size > 0 && (
                <button
                  type="button"
                  onClick={handleClearSelection}
                  className="text-sm text-red-600 hover:text-red-700 font-medium"
                >
                  Clear Selection
                </button>
              )}
            </div>

            {/* Mode Tabs */}
            <div className="flex border-b border-gray-200 mb-4">
              <button
                type="button"
                onClick={() => setAssignmentMode('individual')}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  assignmentMode === 'individual'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                👥 Individual Students
              </button>
              <button
                type="button"
                onClick={() => setAssignmentMode('class')}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  assignmentMode === 'class'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                📚 Classes
              </button>
            </div>

            {assignmentMode === 'class' ? (
              // Class Selection
              <div>
                <div className="flex justify-between items-center mb-3">
                  <span className="text-sm text-gray-600">Select classes to assign all their students</span>
                </div>

                {classesLoading ? (
                  <div className="text-center py-8 border border-gray-200 rounded-lg">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
                    <p className="text-gray-500 mt-2 text-sm">Loading classes...</p>
                  </div>
                ) : classes.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 border border-gray-200 rounded-lg">
                    <p className="text-4xl mb-2">📚</p>
                    <p className="font-medium">No classes imported yet</p>
                    <p className="text-sm mt-1">Connect Google Classroom and import courses to use class assignment.</p>
                    <p className="text-sm mt-1">Or switch to <strong>Individual Students</strong> tab to assign directly.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {classes.map((cls) => {
                      const loadedIds = cls.studentIds ?? [];
                      const allSelected = loadedIds.length > 0 && loadedIds.every(id => selectedStudents.has(id));
                      const displayCount = loadedIds.length > 0 ? loadedIds.length : cls.studentCount;

                      return (
                        <button
                          key={cls.id}
                          type="button"
                          onClick={() => handleClassSelect(cls.id)}
                          className={`p-3 border-2 rounded-lg transition-colors text-left relative ${
                            allSelected
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                          }`}
                        >
                          {allSelected && (
                            <div className="absolute top-2 right-2 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                              <span className="text-white text-xs">✓</span>
                            </div>
                          )}
                          {cls.color && (
                            <div
                              className="w-4 h-4 rounded-full float-right mr-6"
                              style={{ backgroundColor: cls.color }}
                            ></div>
                          )}
                          <div className="font-medium text-gray-900">{cls.name}</div>
                          {(cls.source === 'google_classroom' || cls.readOnly) && (
                            <div className="text-xs text-orange-600 font-medium mt-0.5">
                              📚 Imported from Google Classroom
                            </div>
                          )}
                          <div className="text-sm text-gray-500">{displayCount} students</div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
              // Individual Student Selection
              <div>
                <div className="flex justify-between items-center mb-3">
                  <span className="text-sm text-gray-600">Select individual students</span>
                  <button
                    type="button"
                    onClick={handleSelectAll}
                    className="text-sm text-blue-600 hover:text-blue-700"
                  >
                    {selectedStudents.size === students.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>

                {/* Search bar */}
                <div className="mb-3">
                  <input
                    type="text"
                    placeholder="Search students by name or email..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            )}

            {assignmentMode === 'individual' && (
              loading ? (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
                  <p className="text-gray-500 mt-2">Loading students...</p>
                </div>
              ) : students.length === 0 ? (
                <div className="text-center py-8 text-gray-500 border border-gray-200 rounded-lg">
                  <p className="text-4xl mb-2">👥</p>
                  <p>No students found</p>
                  <p className="text-sm">Make sure students are assigned to your class</p>
                </div>
              ) : (() => {
                const filteredStudents = students.filter(student => {
                  if (!searchQuery) return true;
                  const query = searchQuery.toLowerCase();
                  return (
                    student.displayName.toLowerCase().includes(query) ||
                    student.email.toLowerCase().includes(query)
                  );
                });

                if (filteredStudents.length === 0) {
                  return (
                    <div className="text-center py-8 text-gray-500 border border-gray-200 rounded-lg">
                      <p className="text-4xl mb-2">🔍</p>
                      <p>No students match "{searchQuery}"</p>
                      <p className="text-sm">Try a different search term</p>
                    </div>
                  );
                }

                return (
                  <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg">
                    {filteredStudents.map((student) => (
                      <div
                        key={student.id}
                        className="flex items-center p-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                      >
                      <input
                        type="checkbox"
                        id={`student-${student.id}`}
                        checked={selectedStudents.has(student.id)}
                        onChange={() => handleStudentToggle(student.id)}
                        className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                      />
                      <label
                        htmlFor={`student-${student.id}`}
                        className="ml-3 flex-1 cursor-pointer"
                      >
                        <div className="font-medium text-gray-900">{student.displayName}</div>
                        <div className="text-sm text-gray-500">{student.email}</div>
                      </label>
                    </div>
                  ))}
                </div>
                );
              })()
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || selectedStudents.size === 0}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Creating...' : `Assign to ${selectedStudents.size} Students`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}